// Line Execute — Intent Detector.
//
// Cheap regex+slot detector that scores each candidate action kind against a
// prompt. Not a language model — this exists to catch the "obvious" cases
// (verb + object + date, verb + item id, single-clause command) so we can
// route them to a typed adapter before the LLM pipeline burns credits.
//
// Every kind here has a corresponding action registered in
// the unified action registry (src/services/actions/domains/). If a match
// returns but no action is registered, the dispatcher falls through
// gracefully — the detector is allowed to be optimistic.

import type { LineExecuteActionKind } from './lineExecuteKindMap'
import type { LineExecuteIntentMatch } from './dispatcher'

// A single detector rule. If `pattern` matches, we emit `kind` with
// `confidence` and let `extractSlots` pull any structured fields.
interface DetectorRule {
  kind: LineExecuteActionKind
  pattern: RegExp
  confidence: number
  extractSlots?: (match: RegExpMatchArray, prompt: string) => Record<string, unknown>
}

// Scheduling verb + meeting/huddle/sync. The distinguishing signal against
// the legacy MEETING_BRIEF classifier is a scheduling *verb*: "set", "book",
// "schedule", "arrange", "put on the calendar". A plain "meeting" reference
// without a scheduling verb is still a brief and stays on the old path.
const MEETING_SCHEDULE_PATTERN =
  /\b(?:set(?:\s+up)?|book|schedule|arrange|create|make|put(?:\s+on\s+the\s+calendar)?)\b[\s\S]*?\b(?:meeting|huddle|sync|standup|1[- ]?on[- ]?1|catch[- ]?up|call)\b/i

// Cancellation intent.
const MEETING_CANCEL_PATTERN =
  /\b(?:cancel|drop|scrap|remove|delete)\b[\s\S]*?\b(?:meeting|huddle|sync|standup|call)\b/i

// Timeline status change: "mark X done|complete|in progress|blocked".
const TIMELINE_STATUS_PATTERN =
  /\b(?:mark|set|move|change)\b[\s\S]{0,60}?\b(?:done|complete|completed|in\s*progress|todo|to[- ]do|blocked|review)\b/i

// Checklist toggle: explicit "check off" / "tick" / "uncheck".
const CHECKLIST_TOGGLE_PATTERN =
  /\b(?:check\s+off|tick|uncheck|untick|reopen|mark\s+.*\bdone)\b/i

// Group-chat send: "post|send|message ... in #channel" or "tell the team".
const CHAT_SEND_PATTERN =
  /\b(?:post|send|dm|message|tell)\b[\s\S]{0,80}?(?:in\s+#?[\w-]+|to\s+(?:the\s+)?(?:team|group|channel|helper))\b/i

// Member invite: "invite <name> ...".
const MEMBER_INVITE_PATTERN =
  /\b(?:invite|add)\b[\s\S]{0,40}?\b(?:as|to\s+the\s+workspace|as\s+a?\s*(?:helper|admin|member))\b/i

// Report preview: "show the ... report" / "preview report".
const REPORT_PREVIEW_PATTERN =
  /\b(?:show|preview|generate|open)\s+(?:the\s+)?(?:report|summary|dashboard|weekly)\b/i

// Navigation: "go to <route>" / "take me to <route>" / "open <route>".
const NAVIGATION_PATTERN =
  /\b(?:go\s+to|take\s+me\s+to|open|navigate\s+to|jump\s+to)\s+(?:the\s+)?(timeline|checklists?|meetings?|chat|automate?|automations?|reports?|analysis|analytics|insights?|explore|design|hub|members?|settings|heatmap)\b/i

// Explore: "open the demo workspace" / "show explore".
const EXPLORE_PATTERN =
  /\b(?:open|show|preview)\s+(?:the\s+)?(?:demo|explore|public\s+workspace)\b/i

// Slot extractor for meeting-schedule prompts. Captures raw substrings —
// the adapter parses them properly via parseMeetingConciergeRequest.
const extractMeetingScheduleSlots = (_match: RegExpMatchArray, prompt: string) => {
  const lower = prompt.toLowerCase()
  const time = lower.match(/\b(?:at\s*)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/)?.[0] ?? null
  const dayRef = lower.match(/\b(today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday|\d{1,2}(?:st|nd|rd|th)?\s+(?:january|february|march|april|may|june|july|august|september|october|november|december))\b/)?.[0] ?? null
  const participants = lower.match(/\bwith\s+([^.,;\n]+?)(?:\s+(?:on|at|for)\s+|$)/)?.[1]?.trim() ?? null
  const duration = lower.match(/\b(?:for\s+)?(\d+)\s*(?:minutes?|mins?|hours?|hrs?)\b/)?.[0] ?? null
  return { time, dayRef, participants, duration }
}

const RULES: DetectorRule[] = [
  { kind: 'MEETING_SCHEDULE', pattern: MEETING_SCHEDULE_PATTERN, confidence: 0.9, extractSlots: extractMeetingScheduleSlots },
  { kind: 'MEETING_CANCEL', pattern: MEETING_CANCEL_PATTERN, confidence: 0.85 },
  { kind: 'TIMELINE_STATUS_CHANGE', pattern: TIMELINE_STATUS_PATTERN, confidence: 0.8 },
  { kind: 'CHECKLIST_TOGGLE_DONE', pattern: CHECKLIST_TOGGLE_PATTERN, confidence: 0.8 },
  { kind: 'CHAT_SEND', pattern: CHAT_SEND_PATTERN, confidence: 0.75 },
  { kind: 'MEMBER_INVITE', pattern: MEMBER_INVITE_PATTERN, confidence: 0.8 },
  { kind: 'REPORT_PREVIEW', pattern: REPORT_PREVIEW_PATTERN, confidence: 0.75 },
  { kind: 'EXPLORE_OPEN_PREVIEW', pattern: EXPLORE_PATTERN, confidence: 0.75 },
  { kind: 'NAVIGATION', pattern: NAVIGATION_PATTERN, confidence: 0.7 },
]

export const detectLineExecuteIntent = (prompt: string): LineExecuteIntentMatch | null => {
  const trimmed = prompt.trim()
  if (!trimmed) return null
  let best: LineExecuteIntentMatch | null = null
  for (const rule of RULES) {
    const match = trimmed.match(rule.pattern)
    if (!match) continue
    const slots = rule.extractSlots ? rule.extractSlots(match, trimmed) : {}
    const candidate: LineExecuteIntentMatch = {
      kind: rule.kind,
      confidence: rule.confidence,
      slots,
      matchedText: match[0],
      reason: `Matched ${rule.kind} pattern.`,
    }
    if (!best || candidate.confidence > best.confidence) best = candidate
  }
  return best
}
